// Texture upload plus mipmap generation. WebGPU has no glGenerateMipmap, so we
// downsample with a fullscreen blit per level, sharing one pipeline across all
// textures of a given format.

export interface TextureOptions {
  label?: string;
  format?: GPUTextureFormat;
  mipmaps?: boolean;
  usage?: GPUTextureUsageFlags;
}

const BLIT_WGSL = /* wgsl */ `
@group(0) @binding(0) var src: texture_2d<f32>;
@group(0) @binding(1) var samp: sampler;

struct VsOut {
  @builtin(position) pos: vec4f,
  @location(0) uv: vec2f,
};

@vertex
fn vs(@builtin(vertex_index) vi: u32) -> VsOut {
  // One oversized triangle covering the viewport.
  let uv = vec2f(f32((vi << 1u) & 2u), f32(vi & 2u));
  var out: VsOut;
  out.uv = uv;
  out.pos = vec4f(uv * vec2f(2.0, -2.0) + vec2f(-1.0, 1.0), 0.0, 1.0);
  return out;
}

@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  return textureSample(src, samp, in.uv);
}
`;

export class MipmapGenerator {
  private readonly device: GPUDevice;
  private readonly module: GPUShaderModule;
  private readonly sampler: GPUSampler;
  private readonly pipelines = new Map<GPUTextureFormat, GPURenderPipeline>();

  constructor(device: GPUDevice) {
    this.device = device;
    this.module = device.createShaderModule({ label: 'mipmap:blit', code: BLIT_WGSL });
    this.sampler = device.createSampler({ minFilter: 'linear', magFilter: 'linear' });
  }

  private pipelineFor(format: GPUTextureFormat): GPURenderPipeline {
    let p = this.pipelines.get(format);
    if (!p) {
      p = this.device.createRenderPipeline({
        label: `mipmap:${format}`,
        layout: 'auto',
        vertex: { module: this.module, entryPoint: 'vs' },
        fragment: { module: this.module, entryPoint: 'fs', targets: [{ format }] },
        primitive: { topology: 'triangle-list' },
      });
      this.pipelines.set(format, p);
    }
    return p;
  }

  /** Fills levels 1..mipLevelCount-1 from level 0. */
  generate(texture: GPUTexture, encoder?: GPUCommandEncoder): void {
    if (texture.mipLevelCount < 2) return;

    const pipeline = this.pipelineFor(texture.format);
    const enc = encoder ?? this.device.createCommandEncoder({ label: 'mipmap' });

    for (let level = 1; level < texture.mipLevelCount; level++) {
      const bindGroup = this.device.createBindGroup({
        layout: pipeline.getBindGroupLayout(0),
        entries: [
          {
            binding: 0,
            resource: texture.createView({
              baseMipLevel: level - 1, mipLevelCount: 1,
            }),
          },
          { binding: 1, resource: this.sampler },
        ],
      });

      const pass = enc.beginRenderPass({
        colorAttachments: [{
          view: texture.createView({ baseMipLevel: level, mipLevelCount: 1 }),
          loadOp: 'clear',
          storeOp: 'store',
          clearValue: { r: 0, g: 0, b: 0, a: 0 },
        }],
      });
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bindGroup);
      pass.draw(3);
      pass.end();
    }

    if (!encoder) this.device.queue.submit([enc.finish()]);
  }
}

export const mipLevelsFor = (w: number, h: number): number =>
  1 + Math.floor(Math.log2(Math.max(w, h)));

/** Decodes a URL into an ImageBitmap, flipping nothing -- WebGPU UVs are top-left. */
export async function loadBitmap(url: string): Promise<ImageBitmap> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`texture fetch failed (${res.status}): ${url}`);
  return createImageBitmap(await res.blob(), { colorSpaceConversion: 'none' });
}

export function textureFromBitmap(
  device: GPUDevice,
  bitmap: ImageBitmap,
  mipmaps: MipmapGenerator | null,
  opts: TextureOptions = {},
): GPUTexture {
  const format = opts.format ?? 'rgba8unorm-srgb';
  const wantMips = opts.mipmaps ?? true;
  const mipLevelCount = wantMips ? mipLevelsFor(bitmap.width, bitmap.height) : 1;

  const texture = device.createTexture({
    label: opts.label ?? 'texture',
    size: [bitmap.width, bitmap.height, 1],
    format,
    mipLevelCount,
    usage:
      opts.usage ??
      (GPUTextureUsage.TEXTURE_BINDING |
        GPUTextureUsage.COPY_DST |
        GPUTextureUsage.RENDER_ATTACHMENT),
  });

  device.queue.copyExternalImageToTexture(
    { source: bitmap, flipY: false },
    { texture, premultipliedAlpha: false },
    [bitmap.width, bitmap.height],
  );

  if (mipLevelCount > 1 && mipmaps) mipmaps.generate(texture);
  return texture;
}

export async function loadTexture(
  device: GPUDevice,
  url: string,
  mipmaps: MipmapGenerator | null,
  opts: TextureOptions = {},
): Promise<GPUTexture> {
  const bitmap = await loadBitmap(url);
  try {
    return textureFromBitmap(device, bitmap, mipmaps, { label: url, ...opts });
  } finally {
    bitmap.close();
  }
}

/** 1x1 texture used as a stand-in while real art streams in. */
export function solidTexture(
  device: GPUDevice,
  rgba: [number, number, number, number],
  format: GPUTextureFormat = 'rgba8unorm-srgb',
): GPUTexture {
  const texture = device.createTexture({
    label: 'texture:solid',
    size: [1, 1, 1],
    format,
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
  });
  device.queue.writeTexture(
    { texture },
    new Uint8Array(rgba),
    { bytesPerRow: 4 },
    [1, 1],
  );
  return texture;
}
